import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import Placeholder from '@tiptap/extension-placeholder'
import { TextStyle, FontFamily } from '@tiptap/extension-text-style'
import { useEffect } from 'react'
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Link as LinkIcon, Quote, Undo, Redo, Heading2, Minus,
} from 'lucide-react'

/**
 * RichTextEditor — TipTap-backed HTML editor for compose + reply.
 * Output is real HTML suitable for Gmail API send.
 *
 * Props:
 *   value (string HTML)      — controlled HTML
 *   onChange(html, text)      — fires on user edits; text is plain-text version
 *   placeholder (string)      — ghost text when empty
 *   minHeight (px)            — default 200
 *   disabled (bool)
 *   autoFocus (bool)
 */
export default function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Write your message…',
  minHeight = 200,
  disabled = false,
  autoFocus = false,
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      TextStyle.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            fontSize: {
              default: null,
              parseHTML: el => el.style.fontSize || null,
              renderHTML: attrs => attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {},
            },
          }
        },
      }),
      FontFamily,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class: 'text-navy-600 underline hover:text-navy-800',
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    editable: !disabled,
    autofocus: autoFocus,
    onUpdate: ({ editor }) => {
      if (!onChange) return
      const html = editor.getHTML()
      const text = editor.getText()
      // Treat visually-empty content as empty string
      const isEmpty = editor.isEmpty
      onChange(isEmpty ? '' : html, text)
    },
  }, [])

  // Keep editor in sync if `value` changes externally (e.g. prefill reset)
  useEffect(() => {
    if (!editor) return
    const current = editor.getHTML()
    if ((value || '') !== current) {
      editor.commands.setContent(value || '', false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  if (!editor) return null

  const btn = (active) =>
    `p-1.5 rounded transition-colors text-warm-600 hover:bg-warm-100 hover:text-warm-900 ${
      active ? 'bg-navy-50 text-navy-700' : ''
    } disabled:opacity-40`

  const promptForLink = () => {
    const prev = editor.getAttributes('link').href
    const url = window.prompt('Enter URL (leave empty to remove)', prev || 'https://')
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  return (
    <div className="border border-warm-300 rounded-lg focus-within:border-navy-400 focus-within:ring-2 focus-within:ring-navy-100 transition-colors bg-white">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-warm-200 flex-wrap">
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} disabled={disabled} className={btn(editor.isActive('bold'))} title="Bold (⌘B)">
          <Bold size={14} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} disabled={disabled} className={btn(editor.isActive('italic'))} title="Italic (⌘I)">
          <Italic size={14} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} disabled={disabled} className={btn(editor.isActive('underline'))} title="Underline (⌘U)">
          <UnderlineIcon size={14} />
        </button>
        <div className="w-px h-5 bg-warm-200 mx-0.5" />
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} disabled={disabled} className={btn(editor.isActive('heading', { level: 2 }))} title="Heading">
          <Heading2 size={14} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} disabled={disabled} className={btn(editor.isActive('bulletList'))} title="Bullet list">
          <List size={14} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} disabled={disabled} className={btn(editor.isActive('orderedList'))} title="Numbered list">
          <ListOrdered size={14} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} disabled={disabled} className={btn(editor.isActive('blockquote'))} title="Quote">
          <Quote size={14} />
        </button>
        <div className="w-px h-5 bg-warm-200 mx-0.5" />
        <button type="button" onClick={promptForLink} disabled={disabled} className={btn(editor.isActive('link'))} title="Add link">
          <LinkIcon size={14} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().setHorizontalRule().run()} disabled={disabled} className={btn(false)} title="Horizontal rule">
          <Minus size={14} />
        </button>
        <div className="w-px h-5 bg-warm-200 mx-0.5" />
        <select
          title="Font family"
          disabled={disabled}
          className="text-xs px-1.5 py-1 border border-warm-300 rounded bg-white text-warm-700 hover:border-warm-400 focus:outline-none focus:border-navy-400 h-[26px]"
          value={editor.getAttributes('textStyle').fontFamily || ''}
          onChange={e => {
            const val = e.target.value
            if (val === '') {
              editor.chain().focus().unsetFontFamily().run()
            } else {
              editor.chain().focus().setFontFamily(val).run()
            }
          }}
        >
          <option value="">Default</option>
          <option value="Arial, sans-serif">Arial</option>
          <option value="Georgia, serif">Georgia</option>
          <option value="Times New Roman, serif">Times</option>
          <option value="Courier New, monospace">Courier</option>
          <option value="Trebuchet MS, sans-serif">Trebuchet</option>
        </select>
        <select
          title="Font size"
          disabled={disabled}
          className="text-xs px-1.5 py-1 border border-warm-300 rounded bg-white text-warm-700 hover:border-warm-400 focus:outline-none focus:border-navy-400 h-[26px]"
          value={editor.getAttributes('textStyle').fontSize?.replace('px', '') || '14'}
          onChange={e => {
            const val = e.target.value
            editor.chain().focus().setMark('textStyle', { fontSize: val === '14' ? null : `${val}px` }).run()
          }}
        >
          {['12','13','14','16','18','20'].map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <div className="w-px h-5 bg-warm-200 mx-0.5" />
        <button type="button" onClick={() => editor.chain().focus().undo().run()} disabled={disabled || !editor.can().undo()} className={btn(false)} title="Undo (⌘Z)">
          <Undo size={14} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().redo().run()} disabled={disabled || !editor.can().redo()} className={btn(false)} title="Redo (⌘⇧Z)">
          <Redo size={14} />
        </button>
      </div>

      {/* Editor surface */}
      <EditorContent
        editor={editor}
        className="chatrone-rich-content px-3 py-2.5 focus:outline-none text-sm"
        style={{ minHeight: `${minHeight}px` }}
      />
    </div>
  )
}
